import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useVendorName } from './PredictInventoryVendor';
import { SAMPLE_VENDOR, VENDOR_SHADES, monogram, productCount, productsLine } from './predictWatchlistModel';
import { sourceWords } from './predictSbomSources';
import './predictVendorCell.css';

const shadeShape = PropTypes.shape({ bg: PropTypes.string, ink: PropTypes.string, line: PropTypes.string });

// A Prevent vendor as its monogram, in its shade from vendorShades; SAMPLE_VENDOR
// is a dashed "S". The watch list's Vendor menu shows the same marks, so they
// double as the legend.
export const VendorMonogram = ({ vendorId, name, shade }) => {
  const sample = vendorId === SAMPLE_VENDOR;
  const tone = sample ? null : shade || VENDOR_SHADES[0];
  return (
    <span
      className={`pd-vmono${sample ? ' pd-vmono--sample' : ''}`}
      role="img"
      title={name}
      aria-label={name}
      data-vendor={vendorId}
      style={tone ? { background: tone.bg, color: tone.ink, borderColor: tone.line } : undefined}
    >
      {sample ? 'S' : monogram(name)}
    </span>
  );
};
VendorMonogram.propTypes = {
  vendorId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  shade: shadeShape
};

const POPOVER_WIDTH = 340;
const EDGE = 8;

// How one vendor put the project in scope, its source codes worded for that vendor (predictSbomSources):
// "installed packages, Red Hat SBOM · 3 products".
const howLine = ({ sources, products }, vendorName) =>
  [sources.map(code => sourceWords(code, vendorName)).join(', '), products.length ? productCount(products.length) : '']
    .filter(Boolean)
    .join(' · ');

/**
 * Why a project is in the customer's Predict scope, on one line: a monogram per
 * Prevent vendor whose inventory puts it there, most products first. Hover or
 * focus shows, per vendor, how (its sources) and inside which products. `blocks`
 * is vendorBlocks' answer; [] is a planted demo project. A click on the cell
 * still reaches its row; one inside the popover doesn't.
 */
const VendorCell = ({ blocks, shades }) => {
  const nameOf = useVendorName();
  const popId = useId();
  const cellRef = useRef(null);
  const popRef = useRef(null);
  const [place, setPlace] = useState(null);
  const open = place !== null;

  const show = () => {
    const rect = cellRef.current.getBoundingClientRect();
    const left = Math.max(EDGE, Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - EDGE));
    setPlace({ top: rect.bottom, left, cellTop: rect.top, measured: false });
  };
  const hide = () => setPlace(null);

  // Before the first paint: one that would run past the window's bottom opens above the cell.
  useLayoutEffect(() => {
    if (!place || place.measured) return;
    const height = popRef.current ? popRef.current.offsetHeight : 0;
    const above = place.top + height > window.innerHeight && place.cellTop - height >= 0;
    setPlace({ ...place, top: above ? place.cellTop - height : place.top, measured: true });
  }, [place]);

  // It sits in the window, so it closes when anything scrolls under it.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setPlace(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const names = blocks.map(block => nameOf(block.vendorId));

  return (
    <div
      ref={cellRef}
      className="pd-vcell"
      tabIndex={0}
      aria-describedby={open ? popId : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onKeyDown={event => {
        if (event.key === 'Escape') hide();
      }}
    >
      <span className="pd-vcell__names">{names.length ? names.join(', ') : 'Sample data'}</span>
      <span className="pd-vcell__marks" aria-hidden="true">
        {blocks.length ? (
          blocks.map((block, index) => (
            <VendorMonogram
              key={block.vendorId}
              vendorId={block.vendorId}
              name={names[index]}
              shade={shades.get(block.vendorId)}
            />
          ))
        ) : (
          <VendorMonogram vendorId={SAMPLE_VENDOR} name="Sample data" />
        )}
      </span>
      {open && (
        <div
          id={popId}
          ref={popRef}
          role="tooltip"
          className="pd-vpop"
          data-testid="vendor-popover"
          style={{ top: place.top, left: place.left, visibility: place.measured ? 'visible' : 'hidden' }}
          onClick={event => event.stopPropagation()}
        >
          <div className="pd-vpop__box">
            {blocks.length === 0 && (
              <div className="bz-muted">Planted demo project: no vendor&apos;s inventory puts it in scope.</div>
            )}
            {blocks.map((block, index) => {
              const how = howLine(block, names[index]);
              return (
                <div key={block.vendorId}>
                  <div>
                    <span className="pd-vpop__name">{names[index]}</span>
                    {how && <span className="bz-muted"> · {how}</span>}
                  </div>
                  <div className="bz-muted">{productsLine(block.products)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
VendorCell.propTypes = {
  blocks: PropTypes.arrayOf(
    PropTypes.shape({
      vendorId: PropTypes.string.isRequired,
      products: PropTypes.arrayOf(PropTypes.string).isRequired,
      sources: PropTypes.arrayOf(PropTypes.string).isRequired
    })
  ).isRequired,
  shades: PropTypes.instanceOf(Map).isRequired
};

export default VendorCell;
